import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  MoneyTransitionListItem,
  Query,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const MoneyTransitionDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [moneyTransitionQueryResult, reexecuteMoneyTransitionQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_money_transition.queryString,
    variables: GraphqlOperations.queries.get_money_transition.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const moneyTransition = moneyTransitionQueryResult.data?.get_money_transition;

  const handleUpdateMoneyTransitionSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionMoneyTransitions: MoneyTransitionListItem[] = [],
    response: Subscription,
  ): MoneyTransitionListItem[] => {
    if (response && response.update_money_transition) {
      if (
        response.update_money_transition.transaction_code ===
        moneyTransitionQueryResult.data?.get_money_transition.transaction_code
      ) {
        reexecuteMoneyTransitionQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_money_transition.subscriptionString,
    },
    handleUpdateMoneyTransitionSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.money_transitions} />;
  }

  const title = moneyTransition?.currency
    ? moneyTransition.currency +
      intl.formatMessage({ id: 'moneyTransitions.details.titleCurrencyExtension' })
    : intl.formatMessage({ id: 'moneyTransitions.details.defaultTitle' });
  const titleWithTransactionCode = moneyTransition?.transaction_code
    ? title + ' - ' + moneyTransition.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={moneyTransitionQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={moneyTransition?.status} />
          <h2 className="ml-2">{stringifyStatus(moneyTransition?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="moneyTransitions.details.dataLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.fromAccountLabel' })}
            value={moneyTransition?.from_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.toAccountLabel' })}
            value={moneyTransition?.to_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.amountLabel' })}
            value={formatNumber(moneyTransition?.amount, moneyTransition?.currency)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.currencyLabel' })}
            value={moneyTransition?.currency}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.transitionDateLabel' })}
            value={formatDateTime(moneyTransition?.transfer_date)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransitions.details.fulfillmentDateLabel' })}
            value={formatDateTime(moneyTransition?.fulfillment_date)}
          />
        </BlurredContainerM2030>
      </div>
      {moneyTransition?.status === Status.Active ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="moneyTransitions.details.cancelButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(MoneyTransitionDetails, { fallback: Fallback });
